// Tipos compartidos para el frontend

export enum Role {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  CASHIER = 'CASHIER'
}

export enum PaymentMethod {
  CASH = 'CASH',
  CARD = 'CARD',
  TRANSFER = 'TRANSFER',
  CHECK = 'CHECK'
}

export enum SaleStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum TransferStatus {
  PENDING = 'PENDING',
  IN_TRANSIT = 'IN_TRANSIT',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  locationId?: string;
  location?: Location;
  isActive: boolean;
  // Campos de empleado
  employeeCode?: string;
  commissionRate?: number;
  baseSalary?: number;
  hireDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Location {
  id: string;
  name: string;
  address: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Product {
  id: string;
  code: string;
  alternativeCode?: string;
  alternativeBarcodes?: string[]; // Multiple alternative barcodes
  name: string;
  description?: string;
  sku: string;
  barcode?: string;
  category: string;
  subcategory?: string;
  unit: string;
  costPrice: number;
  salePrice: number;
  // Discount prices for different customer levels
  discountPrice1?: number;
  discountPrice2?: number;
  discountPrice3?: number;
  discountPrice4?: number;
  taxRate: number;
  minStock: number;
  isActive: boolean;
  imageUrl?: string;
  originalCode?: string;
  migratedFrom?: string;
  createdAt: string;
  updatedAt: string;
  inventories?: Inventory[];
}

export interface Inventory {
  id: string;
  productId: string;
  locationId: string;
  quantity: number;
  lastUpdated: string;
  lastCountDate?: string;
  product?: Product;
  location?: Location;
}

export interface Sale {
  id: string;
  locationId: string;
  userId: string;
  customerName?: string;
  subtotal: number;
  discountAmount?: number;
  tax: number;
  total: number;
  paymentMethod: PaymentMethod;
  status: SaleStatus;
  createdAt: string;
  location?: Location;
  user?: User;
  saleItems?: SaleItem[];
  saleDiscounts?: SaleDiscount[];
}

export interface SaleItem {
  id: string;
  saleId: string;
  productId: string;
  quantity: number;
  unitPrice: number;
  total: number;
  product?: Product;
}

export interface Transfer {
  id: string;
  fromLocationId: string;
  toLocationId: string;
  userId: string;
  status: TransferStatus;
  notes?: string;
  createdAt: string;
  completedAt?: string;
  fromLocation?: Location;
  toLocation?: Location;
  items?: TransferItem[];
}

export interface TransferItem {
  id: string;
  transferId: string;
  productId: string;
  quantity: number;
  product?: Product;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  code?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Enums para el sistema de planilla y adelantos
export enum PayrollStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED'
}

export enum AdvanceStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  PAID = 'PAID',
  DEDUCTED = 'DEDUCTED',
  CANCELLED = 'CANCELLED'
}

export enum DiscountType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED_AMOUNT = 'FIXED_AMOUNT'
}

// Interfaces para comisiones
export interface Commission {
  id: string;
  userId: string;
  saleId: string;
  saleTotal: number;
  percentage: number;
  amount: number;
  isPaid: boolean;
  paidAt?: string;
  createdAt: string;
  user?: User;
  sale?: Sale;
}

// Interfaces para planilla
export interface PayrollEntry {
  id: string;
  userId: string;
  periodStart: string;
  periodEnd: string;
  baseSalary: number;
  commissionsTotal: number;
  advancesTotal: number;
  netPay: number;
  status: PayrollStatus;
  paidAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  user?: User;
  commissionIds: string[];
  advanceIds: string[];
}

// Interfaces para adelantos
export interface Advance {
  id: string;
  userId: string;
  amount: number;
  reason?: string;
  status: AdvanceStatus;
  approvedBy?: string;
  approvedAt?: string;
  paidAt?: string;
  deductedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  user?: User;
}

// Interfaces para descuentos
export interface Discount {
  id: string;
  name: string;
  description?: string;
  type: DiscountType;
  percentage?: number;
  fixedAmount?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SaleDiscount {
  id: string;
  saleId: string;
  discountId?: string;
  type: DiscountType;
  name: string;
  percentage?: number;
  fixedAmount?: number;
  discountAmount: number;
  createdAt: string;
}